/**
 * Create: the one place anything new is made.
 *
 * Items, characters, enemies, scenery and quests, each with the questions that
 * belong to it. They were four windows and a tab strip between them, which meant
 * knowing which window made which kind of thing before you could make anything.
 *
 * Whatever is made is usable straight away, without a restart: the server adds
 * it to what it knows, writes it down, and sends the list round again.
 */

import type { ObjectDefinitionWire, QuestWire, SpellbookWire } from '@inverse/protocol';
import { useState } from 'react';
import type { AdminCommand } from '../Admin';
import { EditorCharacters } from './Characters';
import { EditorItems } from './Items';
import { EditorPrayers, EditorSpells } from './Magic';
import type { ItemDraft } from './items';
import { EditorModels } from './Models';
import { EditorSounds, type SoundUpload } from './Sounds';
import { EditorGroups, type GroupArea, type SavedGroup } from './Groups';
import { EditorObjects } from './Objects';
import { EditorPoses, type PoseRow } from './Poses';
import { EditorTriggers, type SavedTrigger, type TriggerArea } from './Triggers';
import type { CharacterDetail, ListedCharacter } from './characters';
import type { ItemDetail, ListedItem } from './items';
import type {
  ItemDefinitionWire,
  ModelWire,
  NpcDefinitionWire,
  RecipeWire,
} from '@inverse/protocol';
import type { Wanted } from './find';

type Maker =
  | 'item'
  | 'npc'
  | 'enemy'
  | 'scenery'
  | 'quest'
  | 'trigger'
  | 'model'
  | 'pose'
  | 'group'
  | 'object'
  | 'spell'
  | 'prayer'
  | 'sound';

const TABS: readonly { kind: Maker; label: string }[] = [
  { kind: 'item', label: 'Items' },
  { kind: 'npc', label: 'NPCs' },
  { kind: 'enemy', label: 'Enemies' },
  { kind: 'object', label: 'Objects' },
  { kind: 'scenery', label: 'Scenery' },
  { kind: 'trigger', label: 'Places' },
  { kind: 'model', label: 'Models' },
  { kind: 'pose', label: 'Animations' },
  { kind: 'group', label: 'Groups' },
  { kind: 'spell', label: 'Spells' },
  { kind: 'prayer', label: 'Prayers' },
  { kind: 'sound', label: 'Sounds' },
];

export function EditorMake({
  objects,
  items,
  itemList,
  itemDetail,
  itemSaves,
  characterList,
  characterDetail,
  characterSaves,
  models,
  onUploadModel,
  onUploadSound,
  onFitModel,
  quests,
  characters,
  shops,
  triggers,
  triggerArea,
  onGo,
  poses,
  clips,
  groups,
  groupArea,
  recipes,
  yields,
  modelUsers,
  onEditItem,
  openOn,
  openThing,
  spellbook,
  onTalk,
  onCommand,
}: {
  /** Every spell and prayer, as the world last sent them. */
  spellbook: SpellbookWire;
  /** What already exists, since new scenery borrows the shape of one of them. */
  objects: readonly ObjectDefinitionWire[];
  items: readonly ItemDefinitionWire[];
  itemList: readonly ListedItem[];
  itemDetail: ItemDetail | null;
  itemSaves: number;
  characterList: readonly ListedCharacter[];
  characterDetail: CharacterDetail | null;
  characterSaves: number;
  models: readonly ModelWire[];
  onUploadModel: (upload: {
    name: string;
    title: string;
    contents: string;
    binary: boolean;
    tags: string[];
  }) => Promise<string | null>;
  onFitModel: (model: ModelWire | null) => void;
  onUploadSound: (upload: SoundUpload) => Promise<string | null>;
  quests: readonly QuestWire[];
  /** The quest the server last sent in full, which is what editing works from. */
  characters: readonly NpcDefinitionWire[];
  /** The shops there are, for anything that opens one. */
  shops: readonly { id: number; name: string }[];
  /** The places in the region that do something to whoever walks in. */
  triggers: readonly SavedTrigger[];
  /** The ground selected in the world, which is where a new place goes. */
  triggerArea: TriggerArea | null;
  onGo: (tx: number, tz: number) => void;
  /** A tab to open on, and a quest to open there, asked for from elsewhere. */
  openOn: Maker | null;
  /** The one thing to open on that tab, from the search across everything. */
  openThing: (Wanted & { kind: string }) | null;
  /** Opens a character's conversation, for a quest step that waits on one. */
  /** And the same, from the list of characters. */
  onTalk: (npcId: number) => void;
  /** What is drawn as each model, for the manager that swaps and removes them. */
  modelUsers: Readonly<
    Record<string, { scenery: string[]; items: string[]; characters: string[] }>
  >;
  /** Opens the window that writes one item. */
  onEditItem: (draft: ItemDraft) => void;
  /** Which clip each pose is set to, and what the model has to offer. */
  poses: readonly PoseRow[];
  clips: readonly string[];
  /** Pieces of the world saved to be put down again, and the ground held now. */
  groups: readonly SavedGroup[];
  groupArea: GroupArea | null;
  /** What the world hangs off its objects, for saying what each one does. */
  recipes: readonly RecipeWire[];
  yields: readonly { objectId: number; itemId: number }[];
  onCommand: AdminCommand;
}): JSX.Element {
  const [kind, setKind] = useState<Maker>('item');

  // Somewhere else asked for a tab. Taken once, so pressing a tab here after
  // that is not undone by the request that opened the window.
  const [taken, setTaken] = useState<Maker | null>(null);
  if (openOn !== null && openOn !== taken) {
    setTaken(openOn);
    setKind(openOn);
  }

  return (
    <>
      <div className="editor-tabs">
        {TABS.map((tab) => (
          <button
            key={tab.kind}
            type="button"
            className={kind === tab.kind ? 'editor-tab on' : 'editor-tab'}
            onClick={() => setKind(tab.kind)}
          >
            {tab.label}
          </button>
        ))}
      </div>

      <div className="editor-body">
        {kind === 'item' ? (
          <EditorItems
            list={itemList}
            detail={itemDetail}
            saves={itemSaves}
            items={items}
            recipes={recipes}
            wanted={openThing?.kind === 'item' ? openThing : null}
            onEdit={onEditItem}
            onCommand={onCommand}
          />
        ) : null}

        {kind === 'npc' || kind === 'enemy' ? (
          <EditorCharacters
            kind={kind}
            list={characterList}
            detail={characterDetail}
            saves={characterSaves}
            wanted={openThing?.kind === 'character' ? openThing : null}
            items={items}
            quests={quests}
            characters={characters}
            objects={objects}
            shops={shops}
            onTalk={onTalk}
            onCommand={onCommand}
          />
        ) : null}

        {kind === 'model' ? (
          <EditorModels
            models={models}
            users={modelUsers}
            wanted={openThing?.kind === 'model' ? openThing : null}
            onCommand={onCommand}
            onUpload={onUploadModel}
            onFit={onFitModel}
          />
        ) : null}

        {kind === 'pose' ? (
          <EditorPoses written={poses} clips={clips} onCommand={onCommand} />
        ) : null}

        {/* Objects and scenery are the same panel seen from two sides: one
            lists what carries a menu and the other what does not. */}
        {kind === 'object' || kind === 'scenery' ? (
          <EditorObjects
            interactive={kind === 'object'}
            objects={objects}
            items={items}
            quests={quests}
            characters={characters}
            links={{ recipes, yields, shops }}
            onCommand={onCommand}
          />
        ) : null}

        {kind === 'spell' ? (
          <EditorSpells
            spells={spellbook.spells}
            world={{ items, quests, characters, objects, shops, models }}
            onCommand={onCommand}
          />
        ) : null}

        {kind === 'prayer' ? (
          <EditorPrayers
            prayers={spellbook.prayers}
            world={{ items, quests, characters, objects, shops }}
            onCommand={onCommand}
          />
        ) : null}

        {kind === 'sound' ? (
          <EditorSounds
            onCommand={onCommand}
            onUpload={onUploadSound}
            spells={spellbook.spells}
            characters={characters}
          />
        ) : null}

        {kind === 'group' ? (
          <EditorGroups groups={groups} area={groupArea} onCommand={onCommand} />
        ) : null}

        {kind === 'trigger' ? (
          <EditorTriggers
            triggers={triggers}
            items={items}
            quests={quests}
            shops={shops}
            area={triggerArea}
            onSave={(trigger) => onCommand('saveTrigger', {}, { json: JSON.stringify(trigger) })}
            onRemove={(id) => onCommand('removeTrigger', { trigger: id }, {})}
            onGo={onGo}
            onRefresh={() => onCommand('listTriggers', {}, {})}
          />
        ) : null}
      </div>
    </>
  );
}
